import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { Send, User, Clock } from 'lucide-react';
import io from 'socket.io-client';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  expert_id: string;
  content: string;
  created_at: string;
}

interface Expert {
  id: string;
  user_id: string;
  full_name: string;
  title: string;
}

const ChatPage: React.FC = () => {
  const { expertId } = useParams<{ expertId: string }>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [expert, setExpert] = useState<Expert | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socket = useRef<any>(null);

  useEffect(() => {
    loadExpert();
    loadMessages();
    initializeSocket();

    return () => {
      if (socket.current) {
        socket.current.disconnect();
      }
    };
  }, [expertId]);

  const initializeSocket = () => {
    socket.current = io(import.meta.env.VITE_SOCKET_URL || '', {
      query: { expertId }
    });

    socket.current.on('message', (message: Message) => {
      setMessages(prev => [...prev, message]);
    });
  };

  const loadExpert = async () => {
    try {
      const { data, error } = await supabase
        .from('experts')
        .select('id, user_id, full_name, title')
        .eq('id', expertId)
        .single();

      if (error) throw error;
      setExpert(data);
    } catch (error) {
      console.error('Error loading expert:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('expert_messages')
        .select('*')
        .or(
          `and(sender_id.eq.${user.id},expert_id.eq.${expertId}),` +
          `and(receiver_id.eq.${user.id},expert_id.eq.${expertId})`
        )
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !expert) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const message = {
        sender_id: user.id,
        receiver_id: expert.user_id, // Send to expert's user_id
        expert_id: expert.id, // Store the expert's ID
        content: newMessage,
        created_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('expert_messages')
        .insert([message]);

      if (error) throw error;

      socket.current.emit('message', message);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-16rem)] flex flex-col">
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4 mb-4">
        {expert && (
          <div className="flex items-center space-x-4">
            <User className="text-primary" size={32} />
            <div>
              <h2 className="font-semibold">{expert.full_name}</h2>
              <p className="text-sm text-muted-foreground">{expert.title}</p>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 bg-card/50 backdrop-blur-sm border border-border rounded-lg p-4 mb-4 overflow-y-auto">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.sender_id === expert?.user_id ? 'justify-start' : 'justify-end'
              }`}
            >
              <div
                className={`max-w-[70%] rounded-lg p-3 ${
                  message.sender_id === expert?.user_id
                    ? 'bg-card/30 text-card-foreground'
                    : 'bg-primary/10 text-primary'
                }`}
              >
                <p>{message.content}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(message.created_at).toLocaleTimeString()}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <form onSubmit={handleSendMessage} className="flex space-x-4">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type your message..."
          className="flex-1 bg-card border border-border rounded-lg px-4 py-2"
        />
        <button
          type="submit"
          className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Send size={20} />
        </button>
      </form>
    </div>
  );
};

export default ChatPage;